import { useState, useCallback, useRef, useEffect } from 'react';
import { useAddToCart, useIncrementCartItem, useDecrementCartItem, useCartQuery } from '../../cart/cart.services';
import { ChevronLeft, ChevronRight, LayoutGrid, GalleryHorizontal } from 'lucide-react';
import { Heading2, Skeleton, Link } from '@shopmuchmore/design-system';
import type { Product, ProductDiscoveryConfig, DiscoveryLayoutType } from '../../home/home.types';
import ProductCard from './ProductCard';

interface ProductDiscoverySectionProps {
  products: Product[];
  config: ProductDiscoveryConfig;
  className?: string;
  isLoading?: boolean;
  columns?: number;
  totalCount?: number;
}

function ProductDiscoverySection({ products, config, className, isLoading, columns, totalCount }: ProductDiscoverySectionProps) {
  const [layout, setLayout] = useState<DiscoveryLayoutType>(config.defaultLayout);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { data: cart } = useCartQuery();
  const { mutate: addToCart } = useAddToCart();
  const { mutate: increment } = useIncrementCartItem();
  const { mutate: decrement } = useDecrementCartItem();
  const carouselRef = useRef<HTMLDivElement>(null);

  const hasMultipleItems = totalCount !== undefined ? totalCount > 1 : products.length > 1;

  // Calculate items per view based on screen size
  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(2);
      } else if (window.innerWidth < 768) {
        setItemsPerView(3);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(columns === 4 ? 3 : 4);
      } else if (window.innerWidth < 1280) {
        setItemsPerView(columns === 4 ? 4 : 5);
      } else {
        setItemsPerView(columns === 4 ? 4 : 5);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, [columns]);

  const gridColsClass = columns === 4
    ? "grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4"
    : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5";

  const totalPages = Math.ceil(products.length / itemsPerView);

  const handleQuantityChange = useCallback((productSlug: string, newQuantity: number) => {
    const cartItem = cart?.items.find(i => i.product.product_slug === productSlug);
    const current = cartItem?.quantity ?? 0;

    if (current === 0 && newQuantity > 0) {
      addToCart(productSlug);
    } else if (cartItem) {
      if (newQuantity > current) {
        increment(cartItem.id);
      } else if (newQuantity < current) {
        decrement(cartItem.id);
      }
    }
  }, [cart, addToCart, increment, decrement]);


  const goToNext = useCallback(() => {
    if (totalPages === 0) return;
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const goToPrev = useCallback(() => {
    if (totalPages === 0) return;
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  }, [totalPages]);

  const goToPage = (index: number) => {
    setCurrentIndex(index);
  };

  // Touch handling
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrev();
    }

    // Reset
    setTouchStart(0);
    setTouchEnd(0);
  };


  if (!isLoading && products.length === 0) {
    return null; // Don't show empty sections
  }

  return (
    <section className={`w-full max-w-7xl mx-auto ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-10">
        <div className="flex items-center gap-3">
          <Heading2 className="text-foreground font-semibold mb-0 text-2xl">{config.title}</Heading2>
        </div>

        {/* Layout Toggle */}
        {config.showLayoutToggle && !isLoading && !config.viewAllLink && hasMultipleItems && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setLayout('grid')}
              className={`p-2 rounded-md transition-all ${layout === 'grid'
                ? 'bg-white border border-gray-200 text-primary'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              aria-label="Grid view"
            >
              <LayoutGrid className="size-4" />
            </button>
            <button
              onClick={() => setLayout('carousel')}
              className={`p-2 rounded-md transition-all ${layout === 'carousel'
                ? 'bg-white border border-gray-200 text-primary'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              aria-label="Carousel view"
            >
              <GalleryHorizontal className="size-4" />
            </button>
          </div>
        )}
        {config.viewAllLink && !isLoading && hasMultipleItems && (
          <Link
            size="sm"
            variant="default"
            trailingIcon={<ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />}
            href={config.viewAllLink}

          >
            View All
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className={`grid ${gridColsClass} gap-3 md:gap-4 py-4`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="aspect-square w-full rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Grid Layout */}
          {layout === 'grid' && (
            <div className={`grid ${gridColsClass} gap-3 md:gap-4 py-4`}>
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  widgetType={config.widgetType}
                  onQuantityChange={handleQuantityChange}
                />
              ))}
            </div>
          )}

          {/* Carousel Layout */}
          {layout === 'carousel' && (
            <div className="relative">
              {/* Carousel Container */}
              <div
                ref={carouselRef}
                className="overflow-hidden"
              >
                <div
                  className="flex transition-transform duration-500 ease-out py-2"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {/* Create pages of items */}
                  {Array.from({ length: totalPages }).map((_, pageIndex) => (
                    <div
                      key={pageIndex}
                      className={`w-full shrink-0 grid ${gridColsClass} gap-3 md:gap-4`}
                    >
                      {products
                        .slice(pageIndex * itemsPerView, (pageIndex + 1) * itemsPerView)
                        .map((product) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            widgetType={config.widgetType}
                            onQuantityChange={handleQuantityChange}
                          />
                        ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Arrows */}
              {totalPages > 1 && (
                <>
                  <button
                    onClick={goToPrev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:scale-105 transition-all z-10"
                    aria-label="Previous"
                  >
                    <ChevronLeft className="size-5" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:scale-105 transition-all z-10"
                    aria-label="Next"
                  >
                    <ChevronRight className="size-5" />
                  </button>
                </>
              )}

              {/* Pagination Dots */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToPage(index)}
                      className={`h-2 rounded-full transition-all ${index === currentIndex
                        ? 'w-6 bg-primary'
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                        }`}
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default ProductDiscoverySection;
